import React from 'react';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@/src/shadComponents/ui/card';
import { PaymentReportData } from './hooks/useActivationPaymentSettings';

interface PaymentReportChartProps {
    reportData: PaymentReportData;
}

export default function PaymentReportChart({
    reportData,
}: PaymentReportChartProps) {
    const { dailyChartData } = reportData;

    if (!dailyChartData || dailyChartData.length === 0) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle>Payment Activity (Last 30 Days)</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex items-center justify-center h-64 text-gray-500">
                        No payment data available for the last 30 days
                    </div>
                </CardContent>
            </Card>
        );
    }

    // Find max values for scaling
    const maxPayments = Math.max(...dailyChartData.map((d) => d.count), 1);
    const maxRevenue = Math.max(
        ...dailyChartData.map((d) => d.revenue || 0),
        1,
    );

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
        });
    };

    const formatCurrency = (cents: number) => {
        return `$${(cents / 100).toFixed(2)}`;
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle>Payment Activity (Last 30 Days)</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="space-y-6">
                    {/* Payment Count Chart */}
                    <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-3">
                            Daily Payment Count
                        </h4>
                        <div className="relative h-32">
                            <div className="flex items-end justify-between h-full space-x-1">
                                {dailyChartData.map((data, index) => {
                                    const height =
                                        (data.count / maxPayments) * 100;
                                    return (
                                        <div
                                            key={index}
                                            className="flex-1 flex flex-col items-center group relative"
                                        >
                                            {/* Bar */}
                                            <div
                                                className="w-full bg-blue-500 hover:bg-blue-600 transition-colors rounded-t-sm min-h-[2px]"
                                                style={{ height: `${height}%` }}
                                            />

                                            {/* Tooltip on hover */}
                                            <div className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
                                                <div>
                                                    {formatDate(data.date)}
                                                </div>
                                                <div>{data.count} payments</div>
                                                <div>
                                                    {formatCurrency(
                                                        data.revenue || 0,
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Y-axis labels */}
                            <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-xs text-gray-500 -ml-8">
                                <span>{maxPayments}</span>
                                <span>{Math.floor(maxPayments / 2)}</span>
                                <span>0</span>
                            </div>
                        </div>

                        {/* X-axis labels */}
                        <div className="flex justify-between mt-2 text-xs text-gray-500">
                            <span>
                                {dailyChartData.length > 0
                                    ? formatDate(dailyChartData[0].date)
                                    : ''}
                            </span>
                            <span>
                                {dailyChartData.length > 0
                                    ? formatDate(
                                          dailyChartData[
                                              dailyChartData.length - 1
                                          ].date,
                                      )
                                    : ''}
                            </span>
                        </div>
                    </div>

                    {/* Revenue Chart */}
                    <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-3">
                            Daily Revenue
                        </h4>
                        <div className="relative h-32">
                            <div className="flex items-end justify-between h-full space-x-1">
                                {dailyChartData.map((data, index) => {
                                    const height =
                                        ((data.revenue || 0) / maxRevenue) *
                                        100;
                                    return (
                                        <div
                                            key={index}
                                            className="flex-1 flex flex-col items-center group relative"
                                        >
                                            {/* Bar */}
                                            <div
                                                className="w-full bg-green-500 hover:bg-green-600 transition-colors rounded-t-sm min-h-[2px]"
                                                style={{ height: `${height}%` }}
                                            />

                                            {/* Tooltip on hover */}
                                            <div className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
                                                <div>
                                                    {formatDate(data.date)}
                                                </div>
                                                <div>{data.count} payments</div>
                                                <div>
                                                    {formatCurrency(
                                                        data.revenue || 0,
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Y-axis labels */}
                            <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-xs text-gray-500 -ml-16">
                                <span>{formatCurrency(maxRevenue)}</span>
                                <span>
                                    {formatCurrency(Math.floor(maxRevenue / 2))}
                                </span>
                                <span>$0.00</span>
                            </div>
                        </div>

                        {/* X-axis labels */}
                        <div className="flex justify-between mt-2 text-xs text-gray-500">
                            <span>
                                {dailyChartData.length > 0
                                    ? formatDate(dailyChartData[0].date)
                                    : ''}
                            </span>
                            <span>
                                {dailyChartData.length > 0
                                    ? formatDate(
                                          dailyChartData[
                                              dailyChartData.length - 1
                                          ].date,
                                      )
                                    : ''}
                            </span>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
